import VerifiedUser from '@mui/icons-material/VerifiedUser';
import {
  Box,
  FormGroupLabel,
  Icon,
  RadioSelectableCard,
  Spacer,
  Stack,
  Typography,
  useTheme,
} from '@qbe/ads-core';
import React, { useEffect, useMemo } from 'react';

import BankAccountForm from '~/components/BankAccountForm';
import AmexIcon from '~/components-ds/PaymentIcons/AmexIcon';
import MastercardIcon from '~/components-ds/PaymentIcons/MastercardIcon';
import VisaIcon from '~/components-ds/PaymentIcons/VisaIcon';
import { Option, useAppContent, useAppDispatch, useOptionSelect, usePayments } from '~/hooks';
import {
  clearPaymentErrors,
  resetBankAccountForm,
  resetCreditCardForm,
  updatePaymentMethod,
} from '~/slices/payments';
import { PaymentFrequencyOptions, PaymentMethodOptions } from '~/types/payments';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import { CreditCardPayment } from './CreditCardPayment';

interface PaymentMethodProps {
  onTouch?: () => void;
  onSubmitPayment: () => Promise<void>;
}

const toValue = {
  [PaymentMethodOptions.CreditCard]: 'Credit/debit',
  [PaymentMethodOptions.BankAccount]: 'Bank account',
};

const options: Option[] = Object.values(PaymentMethodOptions).map((option) => ({
  id: option,
  value: toValue[option],
}));

const PaymentMethod = ({ onTouch, onSubmitPayment }: PaymentMethodProps) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.paymentMethodComponent',
  });

  const { paymentFrequency, paymentMethod } = usePayments();
  const dispatch = useAppDispatch();
  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options,
  });
  const theme = useTheme();

  useEffect(() => {
    dispatch(resetCreditCardForm());
  }, [dispatch]);

  useEffect(() => {
    setSelectedIDs([paymentMethod!]);
  }, [paymentFrequency, paymentMethod, setSelectedIDs]);

  const resetForm = (paymentType: PaymentMethodOptions) => {
    dispatch(clearPaymentErrors());
    paymentType === PaymentMethodOptions.BankAccount
      ? dispatch(resetCreditCardForm())
      : dispatch(resetBankAccountForm());
  };

  const onChange = (id: string) => {
    onTouch && onTouch();

    if (!selectedIDs.includes(id)) {
      onSelectID(id);
      const paymentType = id as PaymentMethodOptions;
      dispatch(updatePaymentMethod(paymentType));
      resetForm(paymentType);
    }
  };

  const radioList = useMemo(() => {
    const newOptions =
      paymentFrequency === PaymentFrequencyOptions.Monthly
        ? options
        : options.filter((option) => option.id !== PaymentMethodOptions.BankAccount);

    return newOptions.map(({ id, value }) => ({
      checked: id === paymentMethod,
      id,
      value: id,
      label: value,
      ...(id === PaymentMethodOptions.CreditCard
        ? {
            customComponent: (
              <Stack direction="row" marginTop="-0.5em" marginBottom="-0.5em" spacing={1}>
                <VisaIcon fontSize="large" />
                <MastercardIcon fontSize="large" />
                <AmexIcon fontSize="large" />
              </Stack>
            ),
          }
        : {}),
      'data-track': value,
      'data-track-value': 'capture',
    }));
  }, [paymentFrequency, paymentMethod]);

  return (
    <Box width="100%" maxWidth="400px" textAlign="center" data-testid="payment-method">
      <FormGroupLabel
        title={getTextContent('title')}
        legendElementOveride
        alignCenter
        titlePrefix={
          <Icon
            muiIcon={VerifiedUser}
            // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            color={theme.color.icon.info}
            size={24}
          />
        }
        titleSuffix={
          <ContextualHelpWithButton tracking="help: How would you like to pay?">
            <Typography>{getJSXContent('content.contextualHelper.description')}</Typography>
          </ContextualHelpWithButton>
        }
      />

      <RadioSelectableCard
        title=""
        data-testid="payment-method"
        name="method"
        radioList={radioList}
        direction="vertical"
        onChange={(_id, value) => onChange(value.id)}
        defaultValue={paymentMethod}
      />

      {paymentMethod && (
        <>
          <Spacer size="md" />
          {paymentMethod === PaymentMethodOptions.CreditCard ? (
            <CreditCardPayment onSubmitPayment={onSubmitPayment} />
          ) : (
            <BankAccountForm onSubmitPayment={onSubmitPayment} />
          )}
        </>
      )}
    </Box>
  );
};

export default PaymentMethod;
